import {
  prepareElementsForExport,
  getFullContentDimensions,
  prepareClonedDocumentForExport,
  injectExportStyles,
  removeExportStyles
} from './exportHelpers';

/**
 * Exporta o canvas para PDF com tamanho de folha proporcional ao mapa.
 * Funciona nos modos Atual, Kaizen e Futuro.
 */
export const exportCanvasToPDF = async (
  canvasElement: HTMLElement,
  projectName: string = 'Workflow'
): Promise<void> => {
  let exportStyleElement: HTMLStyleElement | null = null;

  try {
    console.log('[exportPDF] Iniciando exportação PDF...');

    // Importar dinamicamente
    const html2canvas = (await import('html2canvas')).default;
    const { jsPDF } = await import('jspdf');

    // Injetar estilos CSS temporários para exportação (mesma lógica do A3)
    exportStyleElement = injectExportStyles();

    // Obter dimensões reais do conteúdo (remove transform, mede tudo, restaura)
    const { width, height } = getFullContentDimensions(canvasElement);

    console.log('[exportPDF] Dimensões do conteúdo:', { width, height });

    if (width <= 0 || height <= 0) {
      throw new Error(`Dimensões inválidas: width=${width}, height=${height}`);
    }

    // Criar canvas da imagem — mesma abordagem provada do exportA3
    const canvas = await html2canvas(canvasElement, {
      backgroundColor: '#ffffff',
      scale: 2, // Boa qualidade sem peso excessivo
      useCORS: true,
      logging: false,
      allowTaint: true,
      scrollX: 0,
      scrollY: 0,
      x: 0,
      y: 0,
      width,
      height,
      windowWidth: width,
      windowHeight: height,
      onclone: (clonedDoc) => {
        // Preparar clone: reseta transform, fixa overflow, protege losangos
        prepareClonedDocumentForExport(clonedDoc, 'canvas-content');
        const clonedCanvasContent = clonedDoc.querySelector('#canvas-content') as HTMLElement;
        if (clonedCanvasContent) {
          // Reset transform do clone para coordenadas absolutas (sem pan/zoom)
          clonedCanvasContent.style.transform = 'none';
          clonedCanvasContent.style.width = `${width}px`;
          clonedCanvasContent.style.height = `${height}px`;
          prepareElementsForExport(clonedCanvasContent);
        }
      },
    });

    // Remover estilos temporários
    removeExportStyles(exportStyleElement);
    exportStyleElement = null;

    // Verificar se o canvas tem conteúdo
    if (!canvas || canvas.width === 0 || canvas.height === 0) {
      throw new Error('Canvas vazio ou inválido.');
    }

    console.log('[exportPDF] Canvas gerado:', { width: canvas.width, height: canvas.height });

    const imgData = canvas.toDataURL('image/png', 1.0);

    if (!imgData || imgData.length < 100) {
      throw new Error('Falha ao gerar imagem.');
    }

    // ── Calcular dimensões do PDF ──────────────────────────────────
    // Converter pixels para mm (scale 2× → dividir por 2)
    const mmPerPixel = 0.264583;
    const contentWidthMm = (canvas.width * mmPerPixel) / 2;
    const contentHeightMm = (canvas.height * mmPerPixel) / 2;

    const isLandscape = contentWidthMm > contentHeightMm;

    // Margens e áreas auxiliares
    const margin = 15;
    const headerHeight = 25;
    const footerHeight = 12;
    const pdfWidth = contentWidthMm + margin * 2;
    const pdfHeight = contentHeightMm + margin * 2 + headerHeight + footerHeight;

    const pdf = new jsPDF({
      orientation: isLandscape ? 'landscape' : 'portrait',
      unit: 'mm',
      format: [pdfWidth, pdfHeight],
    });

    // ═══════════════════════════════════════════════════════════════
    // CABEÇALHO PROFISSIONAL
    // ═══════════════════════════════════════════════════════════════
    const logoX = margin;
    const logoY = margin + 2;

    // Ícone workflow (círculos conectados)
    pdf.setFillColor(37, 99, 235);
    pdf.circle(logoX + 3, logoY + 3, 2.5, 'F');
    pdf.setFillColor(16, 185, 129);
    pdf.circle(logoX + 10, logoY + 3, 2.5, 'F');
    pdf.setFillColor(245, 158, 11);
    pdf.circle(logoX + 17, logoY + 3, 2.5, 'F');

    pdf.setDrawColor(100, 116, 139);
    pdf.setLineWidth(0.5);
    pdf.line(logoX + 5.5, logoY + 3, logoX + 7.5, logoY + 3);
    pdf.line(logoX + 12.5, logoY + 3, logoX + 14.5, logoY + 3);

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.setTextColor(30, 41, 59);
    pdf.text('Workflow', logoX + 22, logoY + 4.5);

    // Linha separadora
    pdf.setDrawColor(226, 232, 240);
    pdf.setLineWidth(0.3);
    pdf.line(margin, margin + headerHeight - 5, pdfWidth - margin, margin + headerHeight - 5);

    // Nome do projeto (direita)
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.setTextColor(51, 65, 85);
    const projectNameWidth = pdf.getTextWidth(projectName);
    pdf.text(projectName, pdfWidth - margin - projectNameWidth, logoY + 4.5);

    // ═══════════════════════════════════════════════════════════════
    // CONTEÚDO (MAPA)
    // ═══════════════════════════════════════════════════════════════
    pdf.addImage(
      imgData,
      'PNG',
      margin,
      margin + headerHeight,
      contentWidthMm,
      contentHeightMm,
      undefined,
      'FAST'
    );

    // ═══════════════════════════════════════════════════════════════
    // RODAPÉ PROFISSIONAL
    // ═══════════════════════════════════════════════════════════════
    const footerY = pdfHeight - margin - 2;

    pdf.setDrawColor(226, 232, 240);
    pdf.setLineWidth(0.3);
    pdf.line(margin, footerY - 5, pdfWidth - margin, footerY - 5);

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(100, 116, 139);
    pdf.text(`Exportado em ${new Date().toLocaleString('pt-BR')}`, margin, footerY);

    const signature = 'Gerado por Workflow \u2022 Value Stream Mapping';
    const signatureWidth = pdf.getTextWidth(signature);
    pdf.text(signature, pdfWidth - margin - signatureWidth, footerY);

    // Salvar
    const fileName = `${projectName.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(fileName);

    console.log('[exportPDF] PDF salvo com sucesso:', fileName);
  } catch (error) {
    // Limpar estilos temporários mesmo em caso de erro
    if (exportStyleElement) removeExportStyles(exportStyleElement);
    console.error('[exportPDF] Erro:', error);
    throw new Error('Falha ao exportar PDF. Tente novamente.');
  }
};

